import { StemType } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { ComponentProps, FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import { NEW_DAW_COLORS, styled } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import { ILoopRefStore } from '../../../stores/song/loop/LoopRefStore'
import { INoteCollectionStore } from '../../../stores/song/loop/NoteCollectionStore'
import { deserializeStemFromStorage } from '../../../stores/song/stem/StemUtils'
import { IStudioStore } from '../../../stores/StudioStore'
import { IconButton } from '../../core/Components'
import { ChevronLeft, ChevronRight, PauseIcon, PlayIcon } from '../../core/icons'
import { useStatus } from '../../core/Utils'
import { Spinner } from '../../platform/explore/common/utils'
import useCanvasVisualizer from '../loops/useCanvasVisualizer'

const LOOP_WIDTH = 90
const BUTTON_WIDTH = 25
const MARGIN = 4
const CONTROL_WIDTH = (BUTTON_WIDTH + MARGIN) * 2 // space taken by arrow buttons and margins in px.
const CONTROL_LOOP_WIDTH = LOOP_WIDTH + MARGIN
const LOOP_WIDTH_PX = `${LOOP_WIDTH}px`

export const segmentPlayLoop = (stem: string, artist: string) => {
  window.analytics.track('Button Click', {
    button: 'Play Loop',
    stem: stem,
    artist: artist,
    source: window.location.pathname
  })
}

const StemVisualizerContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  color: '$black'
})

const PreviewContainer = styled('div', {
  width: LOOP_WIDTH_PX,
  overflow: 'visible',
  margin: `0 ${MARGIN}px`,
  display: 'flex',
  pointerEvents: 'none'
})

const LoopContainer = styled('div', {
  pointerEvents: 'auto',
  height: '45px',
  flexBasis: LOOP_WIDTH_PX,
  marginRight: `${MARGIN}px`,
  flexShrink: 0,
  flexGrow: 0,
  borderRadius: '4px',
  boxSizing: 'border-box',
  border: '1px solid transparent',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'stretch',
  padding: '6px',
  transition: 'all 200ms ease',
  position: 'relative',
  '& #playpause': {
    opacity: 0
  },
  '&:hover #playpause': {
    opacity: 0.7
  }
})

const LoopsContainer = styled('div', {
  transition: 'all 200ms ease',
  display: 'flex',
  pointerEvents: 'none'
})

const LoopCanvas = styled('canvas', {
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  opacity: 0.3
})

interface LoopVisualizerProps {
  loop: ILoopRefStore
  isDrums: boolean
  isActive: boolean
  color: string
  isLoading?: boolean
  isPlaying?: boolean
  onClick?: () => void
}

const CircleButton = styled('button', {
  all: 'unset',
  width: '25px',
  height: '25px',
  color: '$black',
  background: '$gray01',
  borderRadius: '999px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  transition: 'all 100ms ease',
  '&:hover': { opacity: 1 },
  '&:active': { opacity: 0.7 }
})

const LoopVisualizer: FC<LoopVisualizerProps> = observer(
  ({ loop, isDrums, isActive, color, isPlaying, onClick, isLoading }) => {
    const notes: INoteCollectionStore = isDrums
      ? loop.drumLoop.condensedNotesForVisualization
      : loop.melodicLoop.notes
    const canvasRef = useCanvasVisualizer(notes, false, undefined, '#000000', isDrums)
    const opacity = isActive ? 1 : 0
    const transform = `scale(${isActive ? 1 : 0.8})`
    const pointerEvents = isActive ? 'auto' : 'none'
    const background = color
    return (
      <LoopContainer css={{ opacity, transform, background, pointerEvents }}>
        <LoopCanvas ref={canvasRef} />
        <CircleButton
          id={'playpause'}
          onClick={onClick}
          style={{ opacity: isPlaying ? 0.7 : undefined }}
        >
          {isLoading ? <Spinner /> : isPlaying ? <PauseIcon /> : <PlayIcon />}
        </CircleButton>
      </LoopContainer>
    )
  }
)

type NewStemVisualizerProps = ComponentProps<typeof StemVisualizerContainer> & {
  stem: IArpMedia
}

const COLORS = Object.values(NEW_DAW_COLORS)
const useRandomColor = (...deps: any[]) =>
  useMemo(() => COLORS[Math.floor(Math.random() * COLORS.length)], deps)

const prepareLoop = async (studio: IStudioStore, stem: IArpMedia, loopIndex: number) => {
  // initialize studio
  await studio.initialize(false)
  // this will ensure that the the studio will be fresh on every preview
  studio.reset()
  // set tempo
  studio.song.updateTempo(stem.metadata.bpm || 120)
  // add stem to the empty song
  studio.addStemFromRemix(stem)
  // fetch samples
  await studio.fetchSamplesForCurrentSongAsync()
  // clear stem arrangement
  studio.song.stems[0].clearArrangement()
  // set the correct loop, +1 because loops start at 1, 0 means blank space
  studio.song.updateLoopForStem(0, 0, loopIndex + 1)
  // toggle repeat section if it's off
  if (!studio.repeatSection) studio.toggleRepeatSection()
}

const usePreviewStem = () => {
  const { player } = useTreeState()

  useEffect(() => {
    player.initialize()
  }, [])

  const activeStemIndex = player.song.stems.length > 0 ? player.song.stems[0].remixedStemId : -1
  const activeLoopIndex =
    player.song.stems.length > 0 ? player.song.stems[0].stemSettings.arrangement[0] - 1 : -1
  const [status, setStatus] = useStatus('ready')
  const handlePreview = async (stem: IArpMedia, loopIndex: number) => {
    if (status === 'loading') return
    // if we're triggering handlePreview on the same loop it should stop
    if (stem.id === activeStemIndex && loopIndex === activeLoopIndex) {
      player.reset()
      return
    }
    setStatus('loading')
    await prepareLoop(player, stem, loopIndex)
    setStatus('ready')
    player.play()
    segmentPlayLoop(stem.metadata.title, stem.user.displayName)
    console.log('playing')
  }
  return {
    handlePreview,
    activeStemIndex,
    activeLoopIndex,
    status,
    isPlaying: player.isPlaying
  }
}

const NewStemVisualizer: FC<NewStemVisualizerProps> = ({ stem, ...props }) => {
  const stemStore = useMemo(
    () => deserializeStemFromStorage(stem.metadata.arpeggiComposition),
    [stem]
  )
  if (!stemStore) {
    console.warn(`Stem ${stem.id} has an invalid composition...`)
    return
  }

  if (stemStore.type() === StemType.AUDIO) {
    return <StemVisualizerContainer onClick={(evt) => evt.stopPropagation()} {...props} />
  }

  const loops = stemStore.loops
  const isDrums = stemStore.type() === StemType.DRUMS
  const stemColor = useRandomColor(stem)
  const [activeIndex, setActiveIndex] = useState(0)
  const { status, handlePreview, activeLoopIndex, activeStemIndex, isPlaying } = usePreviewStem()
  const handleMove = (inc: number) => () => {
    setActiveIndex(activeIndex + inc)
    // if playing the loop and the button is pressed we can play the next loop
    if (isPlaying && activeStemIndex === stem.id)
      handlePreview(stem, activeLoopIndex + inc).catch((err) => console.error(err))
  }

  const [previewNumber, setPreviewNumber] = useState(1)
  const previewWidth = `${previewNumber * CONTROL_LOOP_WIDTH - MARGIN}px`
  const containerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!containerRef) return

    // this will handle all the calculations after the resize of the wrapper container
    const handleResize: ResizeObserverCallback = (entries) => {
      const container = entries[0]
      if (!container) return
      const width = container.contentRect.width
      // previewNumber indicates the number of LOOPS we can fit inside the container
      const previewNumber = Math.floor((width - CONTROL_WIDTH) / CONTROL_LOOP_WIDTH)
      setPreviewNumber(previewNumber || 1)
    }

    // resize observer will listen to the dom updates of the wrapper container and recalculate
    // the number of loops available
    const observer = new ResizeObserver(handleResize)

    observer.observe(container)

    return () => observer.disconnect()
  }, [containerRef])

  const lastActive = activeIndex + previewNumber
  const canMoveLeft = activeIndex > 0
  const canMoveRight = lastActive < loops.length

  return (
    <StemVisualizerContainer ref={containerRef} onClick={(evt) => evt.stopPropagation()} {...props}>
      <IconButton disabled={!canMoveLeft} onClick={handleMove(-1)}>
        <ChevronLeft />
      </IconButton>
      <PreviewContainer css={{ width: previewWidth }}>
        <LoopsContainer css={{ transform: `translateX(-${activeIndex * CONTROL_LOOP_WIDTH}px)` }}>
          {loops.map((loop, i) => (
            <LoopVisualizer
              key={i}
              loop={loop}
              isDrums={isDrums}
              isActive={i >= activeIndex && i < lastActive}
              isLoading={status === 'loading'}
              isPlaying={activeLoopIndex === i && activeStemIndex === stem.id && isPlaying}
              color={stemColor}
              onClick={() => handlePreview(stem, i)}
            />
          ))}
        </LoopsContainer>
      </PreviewContainer>
      <IconButton disabled={!canMoveRight} onClick={handleMove(1)}>
        <ChevronRight />
      </IconButton>
    </StemVisualizerContainer>
  )
}

export default observer(NewStemVisualizer)
